import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isSuperadmin } from '@/lib/utils';
import Sidebar from './Sidebar';
import NotificationCenter from './NotificationCenter';
import CommandSearch from './CommandSearch';
import { User, ChevronDown, LogOut, Settings, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('nivela_sidebar_collapsed');
    if (saved === 'true') setSidebarCollapsed(true);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('nivela_sidebar_collapsed', !prev ? 'true' : 'false');
      return !prev;
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#F7FAFB]" data-testid="app-layout">
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header
          className="h-16 bg-white border-b border-[#0D1830]/10 flex items-center justify-between px-6 sticky top-0 z-50 shrink-0"
          data-testid="top-bar"
        >
          <div className="flex-1">
            <CommandSearch />
          </div>

          <div className="flex items-center gap-2">
            <NotificationCenter />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full px-2 py-1.5 hover:bg-[#F7FAFB] transition-colors" data-testid="user-menu">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#0D1830]/8 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-[#0D1830]" />
                    </div>
                  )}
                  <span className="text-sm font-semibold text-[#0D1830] hidden sm:block max-w-[120px] truncate">{user?.name}</span>
                  {user?.role && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full hidden sm:block uppercase tracking-wide ${
                      user.role === 'superadmin' ? 'bg-amber-100 text-amber-800' :
                      user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      user.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                      user.role === 'estimator' ? 'bg-[#72A4BF]/15 text-[#1D4053]' :
                      'bg-[#F7FAFB] text-[#446E87]'
                    }`}>{user.role}</span>
                  )}
                  <ChevronDown className="w-3 h-3 text-[#72A4BF]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-[#0D1830]">{user?.name}</p>
                  <p className="text-xs text-[#72A4BF] truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />Settings
                  </Link>
                </DropdownMenuItem>
                {isSuperadmin(user) && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin">
                      <ShieldCheck className="w-4 h-4" />Admin Panel
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="nav-logout">
                  <LogOut className="w-4 h-4 mr-2" />Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
